import { createPortal } from "react-dom";
import { X, Trash2 } from "lucide-react";
import { useState, useCallback } from "react";
import { toast } from "react-toastify";

import CustomInfoMessage from "@/components/desktop/list/CustomInfoMessage";
import CustomConfirmModal from "@/components/desktop/modal/CustomConfirmModal";
import { useDeletePhytosanitaryProgram } from "@/hooks/planning/phytosanitary-program/usePhytosanitaryProgram";

import styles from "./progress-modal.module.scss";

const PhytosanitaryProgressModal = ({ open, onClose, progressData }) => {
	const [errorMessages, setErrorMessages] = useState([]);
	const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [programToDelete, setProgramToDelete] = useState(null);

	const { mutate: deleteProgram, isLoading: isLoadingDelete } = useDeletePhytosanitaryProgram();

	const handleDeleteClick = useCallback((programId, programName) => {
		setErrorMessages([]);
		setProgramToDelete({ id: programId, name: programName });
		setDeleteConfirmOpen(true);
	}, []);

	const confirmDelete = useCallback(() => {
		if (programToDelete) {
			setDeleteConfirmOpen(false);

			deleteProgram(programToDelete.id, {
				onSuccess: (response) => {
					toast.success(response?.message || "Programa eliminado correctamente", { toastId: "phytosanitary-program-deleted" });
					setProgramToDelete(null);
					setErrorMessages([]);
					onClose();
				},
				onError: (error) => {
					const messages =
						error?.response?.data?.messages ||
						(Array.isArray(error?.response?.data) ? error.response.data : [error?.response?.data?.message || "Error al eliminar programa"]);

					setErrorMessages(messages);
					setProgramToDelete(null);
				},
			});
		}
	}, [programToDelete, deleteProgram, onClose]);

	const cancelDelete = useCallback(() => {
		setDeleteConfirmOpen(false);
		setProgramToDelete(null);
	}, []);

	const handleClose = useCallback(() => {
		if (isLoadingDelete || isDeleteConfirmOpen) {
			return;
		}
		setErrorMessages([]);
		onClose();
	}, [onClose, isLoadingDelete, isDeleteConfirmOpen]);

	if (!open) return null;

	const hasData = progressData?.data && progressData.data.length > 0;

	const totals = hasData
		? progressData.data.reduce(
				(acc, program) => {
					acc.totalApplications += program.total_applications || 0;
					acc.completedCount += program.completed_count || 0;
					acc.inProgressCount += program.in_progress_count || 0;
					acc.pendingCount += program.pending_count || 0;
					return acc;
				},
				{ totalApplications: 0, completedCount: 0, inProgressCount: 0, pendingCount: 0 },
			)
		: { totalApplications: 0, completedCount: 0, inProgressCount: 0, pendingCount: 0 };

	const percentageCompleted = totals.totalApplications > 0 ? (totals.completedCount * 100) / totals.totalApplications : 0;
	const percentageInProgress = totals.totalApplications > 0 ? (totals.inProgressCount * 100) / totals.totalApplications : 0;

	return createPortal(
		<div className={styles.overlay} onClick={handleClose}>
			<div className={styles.modal} onClick={(e) => e.stopPropagation()}>
				<div className={styles.header}>
					<h2 className={styles.title}>Resumen de Avance</h2>
					<button className={styles.closeButton} onClick={handleClose} type="button">
						<X size={20} strokeWidth={2} />
					</button>
				</div>

				{errorMessages.length > 0 && (
					<div className={styles.errorContainer}>
						<CustomInfoMessage messages={errorMessages} type="error" />
					</div>
				)}

				<div className={styles.container}>
					<div className={styles.summary}>
						<div className={styles.summaryCard}>
							<div className={styles.summaryLabel}>Total Aplicaciones</div>
							<div className={styles.summaryValue}>{totals.totalApplications}</div>
						</div>
						<div className={`${styles.summaryCard} ${styles.completed}`}>
							<div className={styles.summaryLabel}>Completadas</div>
							<div className={styles.summaryValue}>{totals.completedCount}</div>
							<div className={styles.summaryPercentage}>{percentageCompleted.toFixed(1)}%</div>
						</div>
						<div className={`${styles.summaryCard} ${styles.inProgress}`}>
							<div className={styles.summaryLabel}>En Progreso</div>
							<div className={styles.summaryValue}>{totals.inProgressCount}</div>
							<div className={styles.summaryPercentage}>{percentageInProgress.toFixed(1)}%</div>
						</div>
						<div className={`${styles.summaryCard} ${styles.pending}`}>
							<div className={styles.summaryLabel}>Pendientes</div>
							<div className={styles.summaryValue}>{totals.pendingCount}</div>
						</div>
					</div>

					<div className={styles.programs}>
						<h3 className={styles.programsTitle}>Detalle por Programa</h3>
						{!hasData ? (
							<div className={styles.noData}>No hay programas fitosanitarios disponibles</div>
						) : (
							progressData.data.map((program) => (
								<div key={program.program_id} className={styles.program}>
									<div className={styles.programHeader}>
										<div className={styles.programName}>{program.program_name}</div>
										<div className={styles.programActions}>
											<div className={styles.programStats}>
												<span className={styles.statCompleted}>{program.percentage_completed.toFixed(1)}%</span>
												<span className={styles.statInProgress}>{program.percentage_in_progress.toFixed(1)}%</span>
											</div>
											<button
												className={styles.deleteButton}
												onClick={() => handleDeleteClick(program.program_id, program.program_name)}
												title="Eliminar programa"
												type="button"
												disabled={isLoadingDelete}
											>
												<Trash2 size={16} />
											</button>
										</div>
									</div>
									<div className={styles.programDetails}>
										<div className={styles.detail}>
											Total: <strong>{program.total_applications}</strong>
										</div>
										<div className={styles.detail}>
											Completadas: <strong>{program.completed_count}</strong>
										</div>
										<div className={styles.detail}>
											En Progreso: <strong>{program.in_progress_count}</strong>
										</div>
										<div className={styles.detail}>
											Pendientes: <strong>{program.pending_count}</strong>
										</div>
									</div>
									<div className={styles.progressBar}>
										<div className={styles.progressCompleted} style={{ width: `${program.percentage_completed}%` }} />
										<div className={styles.progressInProgress} style={{ width: `${program.percentage_in_progress}%` }} />
									</div>
								</div>
							))
						)}
					</div>
				</div>
			</div>

			<CustomConfirmModal
				open={isDeleteConfirmOpen}
				onClose={cancelDelete}
				onConfirm={confirmDelete}
				title="Confirmar eliminación"
				message={`¿Está seguro de que desea eliminar el programa "${programToDelete?.name}"? Esta acción no se puede deshacer.`}
				confirmText="Sí, eliminar"
				cancelText="No, cancelar"
				isLoading={isLoadingDelete}
			/>
		</div>,
		document.body,
	);
};

export default PhytosanitaryProgressModal;
